import React, { useState, useEffect, useContext } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useHistory } from 'react-router-dom';
import { CustomerContext } from '../../../contexts/CustomerContext';
import Cart from '../../../models/Cart';
import { createOrder } from '../../../api/orders';
import CheckoutForm from './CheckoutForm';
import OrderSummary from './OrderSummary';
import styles from './StripeTestCheckout.module.scss';
import { routes } from '../../../routes';

const stripePromise = loadStripe(process.env.STRIPE_PUBLISHABLE_KEY);

const useCart = (customer) => {
  const [cart, setCart] = useState();

  useEffect(() => {
    if (!customer.cart) return;

    Cart
      .where({ for_email: [customer.cart.id, customer.email] })
      .includes({ orderPackages: { student: {}, apiOption: {}, package: {}, sheets: { pose: {}, background: {} } }, coupon: {} })
      .first()
      .then(response => {
        setCart(response.data);
      });
  }, [customer]);

  return cart;
};

const StripeTestCheckout = () => {
  const history = useHistory();
  const { customer, setCustomer } = useContext(CustomerContext);
  const cart = useCart(customer);

  const [clientSecret, setClientSecret] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [paymentReady, setPaymentReady] = useState(false);
  const [headerHidden, setHeaderHidden] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);

  // Redirect to home if no cart or empty cart (skip after successful order)
  useEffect(() => {
    if (!customer.cart && !orderComplete) {
      history.push('/');
    }
  }, [customer.cart, history, orderComplete]);

  useEffect(() => {
    if (cart && (cart.orderPackages || []).length === 0 && !orderComplete) {
      history.push('/');
    }
  }, [cart, history, orderComplete]);

  // Auto-create payment intent when cart is loaded
  useEffect(() => {
    if (!cart || clientSecret) return;

    const createPaymentIntent = async () => {
      setIsLoading(true);
      setMessage('Creating payment intent...');

      try {
        const response = await fetch('/api/v1/test-payment-intent', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            amount: Math.round(cart.apiTotal * 100),
            currency: 'usd',
          }),
        });

        const data = await response.json();

        if (data.error) {
          setMessage(data.error);
          setClientSecret('');
          setPaymentReady(false);
        } else {
          setClientSecret(data.client_secret);
          setPaymentReady(true);
          setMessage('');
        }
      } catch (error) {
        setMessage('Failed to create payment intent. Please try again.');
        setClientSecret('');
        setPaymentReady(false);
      } finally {
        setIsLoading(false);
      }
    };

    createPaymentIntent();
  }, [cart, clientSecret]);

  const options = {
    clientSecret,
    appearance: {
      theme: 'stripe',
    },
  };

  return (
    <div className={styles.container}>
      <div className={styles.checkoutLayout}>
        <div className={styles.paymentColumn}>
          <div className={styles.card}>
            {!headerHidden && (
              <div className={styles.header}>
                <button
                  onClick={() => history.push(routes.testReview())}
                  className={styles.backLink}
                >
                  ← Back
                </button>
                <h1 className={styles.title}>Checkout</h1>
              </div>
            )}

            {message && !isLoading && (
              <div className={message.includes('succeeded') || message.includes('ready') ? styles.successMessage : styles.errorMessage}>
                {message}
              </div>
            )}

            {isLoading && !paymentReady && (
              <div className={styles.loadingState}>
                <div className={styles.spinner}></div>
                <p>Loading payment form...</p>
              </div>
            )}

            {paymentReady && clientSecret && cart && (
              <Elements stripe={stripePromise} options={options}>
                <CheckoutForm
                  email={customer.email}
                  amount={cart.apiTotal.toFixed(2)}
                  cartId={cart.id}
                  clientSecret={clientSecret}
                  cart={cart}
                  onHeaderHiddenChange={setHeaderHidden}
                  onSuccess={async (paymentIntent, customerData) => {
                    setMessage('Payment succeeded! Creating order...');

                    try {
                      const { billingAddress, shippingAddress } = customerData;

                      // Create the order
                      const orderData = {
                        email: customer.email,
                        first_name: customerData.firstName,
                        last_name: customerData.lastName,
                        phone: customerData.phone,
                        cart_id: cart.id,
                        payment_method: 'stripe_test',
                        paypal_order: JSON.stringify({
                          paymentIntent: paymentIntent?.id,
                          test_checkout: true
                        }),
                        paid_with_paypal: true,
                        // Billing address
                        address: billingAddress?.address1 || '',
                        address2: billingAddress?.address2 || '',
                        city: billingAddress?.city || '',
                        state: billingAddress?.state || '',
                        zip_code: billingAddress?.zip || '',
                        // Shipping address
                        shipping_address: shippingAddress?.address1 || '',
                        shipping_address2: shippingAddress?.address2 || '',
                        shipping_city: shippingAddress?.city || '',
                        shipping_state: shippingAddress?.state || '',
                        shipping_zip: shippingAddress?.zip || '',
                      };

                      const response = await createOrder({ order: orderData });

                      // Mark order complete before clearing cart to prevent home redirect
                      setOrderComplete(true);

                      // Clear session storage and cart
                      sessionStorage.removeItem('test_checkout_cart_id');
                      sessionStorage.removeItem('test_checkout_email');
                      sessionStorage.removeItem('test_checkout_customer_data');
                      setCustomer({ ...customer, cart: null });

                      // Redirect to order complete page
                      history.push(`/order/complete/${response.data.order.id}/${response.data.order.email}`);
                    } catch (error) {
                      setMessage('Payment succeeded but order creation failed. Please contact support.');
                      console.error('Order creation error:', error);
                    }
                  }}
                  onError={(error) => setMessage(error)}
                />
              </Elements>
            )}
          </div>
        </div>

        <div className={styles.summaryColumn}>
          {cart && (
            <OrderSummary cart={cart} amount={cart.apiTotal.toFixed(2)} />
          )}
        </div>
      </div>
    </div>
  );
};

export default StripeTestCheckout;
